import type {
  AnalystForecastApiData,
  AnalystForecastBreakdownItem,
  CorporateEventApiItem,
  FundamentalsApiItem,
  ProgressTermApiActual,
  ProgressTermApiCompanyForecast,
  ProgressTermApiConsensus,
  ProgressTermApiData,
  ScoreApiData,
} from '@app-types/summaryApi';
import type {
  AnalystForecast,
  AnalystRatingHistoryPoint,
  ConsensusSegment,
  ConsensusTrendPoint,
  Divergence,
  EnterpriseScore,
  InvestmentIndicator,
  ProgressChartPoint,
  ProgressStatus,
  ProgressTableCellLine,
  ProgressTableData,
  ProgressTableRow,
  RadarDimension,
  ScheduleItem,
} from '@app-types/performance';
import type { ProgressMetricSelection } from '@constants/progressMetrics';
import { formatNumber, formatPercent, PLACEHOLDER } from '@utils/format';

/**
 * Merges Summary `/score` API values into the existing Enterprise Score view
 * model. The API only provides `key` + numeric values, so Figma-specific copy
 * (labels, axisMax, outlook text, report link) is preserved from `fallback`.
 *
 * Null numeric values stay null. Once the API responds, the UI must show `--`
 * instead of falling back to mock numbers, otherwise a no-data instrument looks
 * like it has valid scores.
 */
export const mapScoreApiToEnterpriseScore = (
  apiData: ScoreApiData | null | undefined,
  fallback: EnterpriseScore,
): EnterpriseScore => {
  const apiDimensions = apiData && Array.isArray(apiData.dimensions) ? apiData.dimensions : [];
  const apiByKey = new Map(apiDimensions.map((dimension) => [dimension.key, dimension]));

  // Preserve the Figma-tuned dimension order and labels from the fallback.
  const dimensions: RadarDimension[] = fallback.dimensions.map((base) => {
    const api = apiByKey.get(base.key);

    return {
      ...base,
      companyValue: api?.companyValue ?? null,
      industryValue: api?.industryValue ?? null,
      chartCompanyValue: api?.companyValue ?? null,
      chartIndustryValue: api?.chartIndustryValue ?? api?.industryValue ?? null,
    };
  });

  return {
    ...fallback,
    updateDate: apiData?.updateDate ?? null,
    totalScore: apiData?.totalScore ?? null,
    industryAverage: apiData?.industryAverage ?? null,
    dimensions,
  };
};

/**
 * Trims only outer whitespace and returns `null` when the value is absent or
 * empty after trimming. Internal Japanese punctuation/spacing/separators are
 * preserved as-is.
 */
const toNullableTrimmedText = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Merges the Enterprise Score outlook fields carried by the Summary
 * `/stocks/analyst-forecast` response into the Enterprise Score view model.
 *
 * The outlook renders inside Enterprise Score, but the values come from the
 * analyst-forecast endpoint, so this mapper is intentionally separate from
 * `mapScoreApiToEnterpriseScore()` (which owns the `/score` numbers).
 *
 * `OUTLOOK_TEXT` is display-ready and already includes the company name, so
 * `name_local` is never prepended. Missing/null/blank values become `null`;
 * they never fall back to the mock company-specific outlook copy.
 */
export const mapAnalystForecastApiToEnterpriseOutlook = (
  apiData: AnalystForecastApiData | null | undefined,
  enterpriseScore: EnterpriseScore,
): EnterpriseScore => ({
  ...enterpriseScore,
  outlookTitle: toNullableTrimmedText(apiData?.OUTLOOK_HEADR),
  outlookText: toNullableTrimmedText(apiData?.OUTLOOK_TEXT),
});

/**
 * Merges Summary `/fundamentals` API values into the existing Investment
 * Indicators view model. The API provides `key` + numeric values but not the
 * display `label`/`unit`, so those stay from `fallback`. Null or missing
 * numeric values stay null once the API has responded.
 */
export const mapFundamentalsApiToInvestmentIndicators = (
  apiItems: FundamentalsApiItem[] | null | undefined,
  fallback: InvestmentIndicator[],
): InvestmentIndicator[] => {
  const items = Array.isArray(apiItems) ? apiItems : [];
  const apiByKey = new Map(items.map((item) => [item.key, item]));

  // Preserve the Figma-tuned order (pbr, per, roe, roa) and labels/units.
  return fallback.map((base) => {
    const api = apiByKey.get(base.key);

    return {
      ...base,
      companyValue: api?.companyValue ?? null,
      industryValue: api?.industryValue ?? null,
      fractionDigits: api && Number.isFinite(api.fractionDigits)
        ? api.fractionDigits
        : base.fractionDigits,
    };
  });
};

/**
 * Merges Summary `/corporate-event` API dates into the existing Schedule view
 * model. The API provides `key` + `date` but not the display `label`, so labels
 * stay from `fallback`. Empty/null/missing dates stay null once the API has
 * responded.
 */
export const mapCorporateEventApiToScheduleItems = (
  apiItems: CorporateEventApiItem[] | null | undefined,
  fallback: ScheduleItem[],
): ScheduleItem[] => {
  const items = Array.isArray(apiItems) ? apiItems : [];
  const apiByKey = new Map(items.map((item) => [item.key, item]));

  // Preserve the Figma-tuned order (earnings, lastRight, exRight, shareholders).
  return fallback.map((base) => {
    const api = apiByKey.get(base.key);

    const date = api?.date?.trim() ? api.date : null;
    return { ...base, date };
  });
};

// --- Analyst forecast -------------------------------------------------------

// API breakdown periods aligned to the Figma consensus-trend order. The
// fallback `consensusTrend` points share this order, so index i maps to the
// same period.
const CONSENSUS_TREND_PERIOD_ORDER = ['12MA', '9MA', '6MA', '3MA', 'CURR'] as const;
// Shared analyst history order (oldest -> newest). Both the Target Price
// history line (`breakdown[].price`) and the rating tooltip metadata
// (`breakdown[].meanRating`) index into this single period array, so the two
// arrays stay aligned by index.
const ANALYST_HISTORY_PERIOD_ORDER = [
  '12MA',
  '11MA',
  '10MA',
  '9MA',
  '8MA',
  '7MA',
  '6MA',
  '5MA',
  '4MA',
  '3MA',
  '2MA',
  '1MA',
  'CURR',
] as const;
const TARGET_HISTORY_AXIS_LABEL_PERIODS = new Set<string>(['9MA', '6MA', '3MA']);
const CONSENSUS_SENTIMENT_KEY_BY_MEAN_LABEL: Record<string, string> = {
  STRONGBUY: 'bull',
  BUY: 'slightBull',
  OUTPERFORM: 'slightBull',
  HOLD: 'neutral',
  NEUTRAL: 'neutral',
  UNDERPERFORM: 'slightBear',
  SELL: 'bear',
};

// Neutral instrument label; the API has no company-name field and the mock
// paragraph must not keep a hardcoded name.
const INSTRUMENT_DISPLAY_NAME = '対象銘柄';

/** API consensus keys → view-model segment keys. */
const SEGMENT_KEY_BY_BREAKDOWN: Record<string, keyof Omit<AnalystForecastBreakdownItem, 'period'>> = {
  bull: 'strongBuy',
  slightBull: 'buy',
  neutral: 'hold',
  slightBear: 'underPerform',
  bear: 'sell',
};

const toNullableNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

/** BA work_items_131: each of these raw consensus fields must be nonzero. */
const hasConsensusCommentData = (
  apiData: AnalystForecastApiData | null | undefined,
): boolean => {
  if (!apiData) return false;

  return [
    apiData.targetPrice,
    apiData.targetPriceHigh,
    apiData.targetPriceLow,
    apiData.recommendation,
    apiData.totalAnalysts,
  ].every((rawValue) => {
    const value = toNullableNumber(rawValue);
    return value !== null && value !== 0;
  });
};

const toCount = (value: number | string | null): number =>
  toNullableNumber(value) ?? 0;

const hasFiniteBreakdownValue = (item: AnalystForecastBreakdownItem): boolean =>
  [
    item.strongBuy,
    item.buy,
    item.hold,
    item.underPerform,
    item.sell,
  ].some((value) => toNullableNumber(value) !== null);

const sumBreakdown = (item: AnalystForecastBreakdownItem): number =>
  toCount(item.strongBuy) +
  toCount(item.buy) +
  toCount(item.hold) +
  toCount(item.underPerform) +
  toCount(item.sell);

const toConsensusSentimentKeyFromMeanLabel = (
  value: string | null | undefined,
): string | null => {
  const normalized = value?.trim().toUpperCase().replace(/[\s_-]+/g, '') ?? '';
  return CONSENSUS_SENTIMENT_KEY_BY_MEAN_LABEL[normalized] ?? null;
};

const toConsensusSentimentKeyFromScore = (value: number | null): string | null => {
  if (value === null) {
    return null;
  }
  if (value >= 4.5) return 'bull';
  if (value >= 3.5) return 'slightBull';
  if (value >= 2.5) return 'neutral';
  if (value >= 1.5) return 'slightBear';
  return 'bear';
};

const toConsensusSentiment = (
  currentBreakdown: AnalystForecastBreakdownItem | undefined,
  apiData: AnalystForecastApiData | null | undefined,
  fallbackSegments: ConsensusSegment[],
): string => {
  const sentimentKey =
    toConsensusSentimentKeyFromMeanLabel(currentBreakdown?.meanLabel) ??
    toConsensusSentimentKeyFromScore(
      toNullableNumber(currentBreakdown?.meanRating ?? apiData?.recommendation ?? null),
    );

  return fallbackSegments.find((segment) => segment.key === sentimentKey)?.label ?? PLACEHOLDER;
};

const toTargetDivergence = (
  average: number | null,
  current: number | null,
): Divergence => {
  if (average === null || current === null || current === 0) {
    return { value: null, percent: null };
  }

  const value = average - current;
  return {
    value,
    percent: (value / current) * 100,
  };
};

const toTargetHistoryMonthLabel = (
  period: (typeof ANALYST_HISTORY_PERIOD_ORDER)[number] | undefined,
  hasApiPeriod: boolean,
  baseDate: Date,
): string | undefined => {
  if (!period || !hasApiPeriod) {
    return undefined;
  }

  const monthsAgo = period === 'CURR'
    ? 0
    : Number(period.match(/^(\d+)MA$/)?.[1]);
  if (!Number.isFinite(monthsAgo)) {
    return undefined;
  }

  const targetMonthIndex = baseDate.getFullYear() * 12 + baseDate.getMonth() - monthsAgo;
  const targetYear = Math.floor(targetMonthIndex / 12);
  const targetMonth = (targetMonthIndex % 12) + 1;

  return `${targetYear}/${String(targetMonth).padStart(2, '0')}`;
};

const toTargetHistoryAxisLabel = (
  period: (typeof ANALYST_HISTORY_PERIOD_ORDER)[number] | undefined,
  monthLabel: string | undefined,
): string | undefined => (
  period && TARGET_HISTORY_AXIS_LABEL_PERIODS.has(period) ? monthLabel : undefined
);

const buildTargetDescription = (params: {
  instrumentName: string;
  analystCount: number | null;
  average: number | null;
  highest: number | null;
  lowest: number | null;
  current: number | null;
  divergencePercent: number | null;
}): string => {
  return `過去3ヵ月間に${params.instrumentName}の予想をした${formatNumber(params.analystCount, 0)}名のウォールストリートのアナリストによる12ヵ月後の予想株価より算出。` +
    `平均目標株価は、${formatNumber(params.average, 2)}、予想最高値は${formatNumber(params.highest, 2)}、` +
    `予想最安値は${formatNumber(params.lowest, 2)}です。` +
    `平均目標株価は、直近の[${formatNumber(params.current, 2)}から${formatPercent(params.divergencePercent, 2)}]の変動を示唆しています。`;
};

/**
 * Merges Summary `/stocks/analyst-forecast` API data into the existing Analyst
 * Forecast view model. Static labels/order stay from `fallback`, but dynamic
 * values that the API does not provide or returns as null are cleared to
 * no-data placeholders instead of keeping mock values.
 */
export const mapAnalystForecastApiToAnalystForecast = (
  apiData: AnalystForecastApiData | null | undefined,
  fallback: AnalystForecast,
): AnalystForecast => {
  const breakdownItems = apiData && Array.isArray(apiData.breakdown) ? apiData.breakdown : [];
  const breakdownByPeriod = new Map(breakdownItems.map((item) => [item.period, item]));
  const currentBreakdown = breakdownByPeriod.get('CURR');

  const average = toNullableNumber(apiData?.targetPrice ?? null);
  const highest = toNullableNumber(apiData?.targetPriceHigh ?? null);
  const lowest = toNullableNumber(apiData?.targetPriceLow ?? null);
  const current = toNullableNumber(apiData?.lastPrice ?? null);
  const divergence = toTargetDivergence(average, current);

  const currBreakdownCount = currentBreakdown && hasFiniteBreakdownValue(currentBreakdown)
    ? sumBreakdown(currentBreakdown)
    : null;
  const totalAnalysts = toNullableNumber(apiData?.totalAnalysts ?? null);
  const analystCount = totalAnalysts !== null && (
    currBreakdownCount === null || totalAnalysts === currBreakdownCount
  )
    ? totalAnalysts
    : currBreakdownCount;

  // Consensus segments: preserve fallback labels/order, replace counts from CURR.
  const segments: ConsensusSegment[] = currentBreakdown
    ? fallback.segments.map((segment) => {
        const breakdownKey = SEGMENT_KEY_BY_BREAKDOWN[segment.key];
        if (!breakdownKey) return { ...segment, count: null };
        return { ...segment, count: toNullableNumber(currentBreakdown[breakdownKey]) };
      })
    : fallback.segments.map((segment) => ({ ...segment, count: null }));

  // Consensus trend: keep fallback labels/order; replace counts per period.
  const consensusTrend: ConsensusTrendPoint[] = fallback.consensusTrend.map((point, index) => {
    const period = CONSENSUS_TREND_PERIOD_ORDER[index];
    const breakdown = period ? breakdownByPeriod.get(period) : undefined;
    if (!breakdown) {
      return {
        ...point,
        bull: null,
        slightBull: null,
        neutral: null,
        slightBear: null,
        bear: null,
      };
    }
    return {
      ...point,
      bull: toNullableNumber(breakdown.strongBuy),
      slightBull: toNullableNumber(breakdown.buy),
      neutral: toNullableNumber(breakdown.hold),
      slightBear: toNullableNumber(breakdown.underPerform),
      bear: toNullableNumber(breakdown.sell),
    };
  });
  const targetHistoryLabelBaseDate = new Date();
  // work_items_78: the visible history line uses `breakdown[].price` in the
  // shared 12MA -> CURR order. Missing/null/blank/non-finite prices map to null
  // (no mock value, no carry-forward). Root `lastPrice` still feeds
  // `targetPrice.current` separately and is not used as a history value.
  const priceHistory = fallback.targetPrice.history.map((point, index) => {
    const period = ANALYST_HISTORY_PERIOD_ORDER[index];
    const breakdown = period ? breakdownByPeriod.get(period) : undefined;
    const monthLabel = toTargetHistoryMonthLabel(
      period,
      Boolean(breakdown),
      targetHistoryLabelBaseDate,
    );
    return {
      ...point,
      // Keep the x-axis sparse for work_items_79, but retain a full month title
      // separately so every available history point can show YYYY/MM on hover.
      label: toTargetHistoryAxisLabel(period, monthLabel),
      tooltipLabel: monthLabel,
      value: toNullableNumber(breakdown?.price ?? null),
    };
  });
  const ratingHistory: AnalystRatingHistoryPoint[] = ANALYST_HISTORY_PERIOD_ORDER.map((
    period,
    index,
  ) => {
    const breakdown = breakdownByPeriod.get(period);
    return {
      key: period,
      label: toTargetHistoryAxisLabel(
        period,
        toTargetHistoryMonthLabel(period, Boolean(breakdown), targetHistoryLabelBaseDate),
      ),
      value: toNullableNumber(breakdown?.meanRating ?? null),
    };
  });

  // Gate only the comment using the five raw fields specified by the BA.
  // Resolved CURR counts/ratings must not replace missing root fields here.
  // Query errors with cached data are still handled at the page boundary.
  const targetDescription = hasConsensusCommentData(apiData) ? buildTargetDescription({
    instrumentName: INSTRUMENT_DISPLAY_NAME,
    analystCount,
    average,
    highest,
    lowest,
    current,
    divergencePercent: divergence.percent,
  }) : '';

  return {
    ...fallback,
    updateDate: toNullableTrimmedText(apiData?.updatedAt),
    targetPrice: {
      ...fallback.targetPrice,
      current,
      average,
      highest,
      lowest,
      history: priceHistory,
    },
    cardAverageDisplay: average,
    divergence,
    analystCount,
    sentiment: toConsensusSentiment(currentBreakdown, apiData, fallback.segments),
    segments,
    consensusTrend,
    ratingHistory,
    targetDescription,
  };
};

// --- Progress term (rate column) -------------------------------------------

/** YYYY/MM/DD (or similar) → comparable YYYYMMDD digits, '' when absent. */
const normalizeDateKey = (date: string | null): string => (date ? date.replace(/\D/g, '') : '');

const toProgressTermValue = (
  item: { value?: number | null; recurringProfit?: number | null } | null | undefined,
): number | null => {
  if (!item) return null;
  return toNullableNumber(item.value !== undefined ? item.value : item.recurringProfit);
};

const isValidActual = (quarter: ProgressTermApiActual): boolean =>
  Boolean(quarter.announcementDate) &&
  toProgressTermValue(quarter) !== null &&
  toNullableNumber(quarter.progressRate) !== null;

const toProgressApiMap = <T extends { period: string }>(items: T[]): Map<string, T> =>
  new Map(items.map((item) => [item.period, item]));

const toDisplayPeriodLabel = (
  period: string,
  fallbackPoints: ProgressChartPoint[],
): string => fallbackPoints.find((point) => point.key === period)?.label ?? period;

const toNoDataProgressChartPoints = (
  fallbackPoints: ProgressChartPoint[],
): ProgressChartPoint[] =>
  fallbackPoints.map((point) => ({
    ...point,
    previous: null,
    current: null,
    latestCompanyForecast: null,
    consensusForecast: null,
  }));

const getProgressChartValues = (points: ProgressChartPoint[]): number[] =>
  points.reduce<number[]>((values, point) => {
    [
      point.previous,
      point.current,
      point.latestCompanyForecast,
      point.consensusForecast,
    ].forEach((value) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        values.push(value);
      }
    });

    return values;
  }, []);

const toNiceProgressChartMax = (
  points: ProgressChartPoint[],
  fallbackMax: number,
): number => {
  const maxValue = Math.max(0, ...getProgressChartValues(points));
  const sourceMax = Math.max(maxValue, fallbackMax);

  if (!Number.isFinite(sourceMax) || sourceMax <= 0) {
    return fallbackMax;
  }

  const targetTickCount = 5;
  const roughInterval = sourceMax / targetTickCount;
  const magnitude = 10 ** Math.floor(Math.log10(roughInterval));
  const normalized = roughInterval / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const interval = niceNormalized * magnitude;

  return interval * Math.ceil(sourceMax / interval);
};

export const mapProgressTermApiToProgressStatusOverview = (
  apiData: ProgressTermApiData | null | undefined,
  activeMetric: ProgressMetricSelection,
  fallback: ProgressStatus,
): Pick<ProgressStatus, 'updateDate' | 'chart' | 'gauge' | 'recent'> => {
  const fallbackPoints = fallback.chart.points;
  const currentActuals = apiData && Array.isArray(apiData.currentActuals)
    ? apiData.currentActuals
    : [];
  const previousActuals = apiData && Array.isArray(apiData.previousActuals)
    ? apiData.previousActuals
    : [];
  const consensus = apiData && Array.isArray(apiData.consensus) ? apiData.consensus : [];
  const currentByPeriod = toProgressApiMap(currentActuals);
  const previousByPeriod = toProgressApiMap(previousActuals);
  const consensusByPeriod = toProgressApiMap(consensus);
  const latestCompanyForecast = toNullableNumber(apiData?.latestCompanyForecast?.value);

  const chartPoints = fallbackPoints.map((point): ProgressChartPoint => {
    const currentActual = currentByPeriod.get(point.key);
    const previousActual = previousByPeriod.get(point.key);
    const consensusPoint = consensusByPeriod.get(point.key);

    return {
      ...point,
      previous: toProgressTermValue(previousActual),
      current: toProgressTermValue(currentActual),
      latestCompanyForecast: point.key === 'fullYear' ? latestCompanyForecast : null,
      consensusForecast: toProgressTermValue(consensusPoint),
    };
  });
  const { gauge, recent } = mapProgressTermApiToProgressStatusRateColumn(
    apiData,
    activeMetric,
    fallback,
  );

  return {
    updateDate: toNullableTrimmedText(apiData?.updatedTime),
    chart: {
      ...fallback.chart,
      max: toNiceProgressChartMax(chartPoints, fallback.chart.max),
      points: apiData ? chartPoints : toNoDataProgressChartPoints(fallbackPoints),
    },
    gauge,
    recent,
  };
};

/**
 * Maps `/progress/term` actuals into the Progress Status rate column
 * (gauge + recent). Picks the latest valid actual by announcement date
 * (including `通期`). The API does not supply display labels, so the selected
 * metric label is applied here. When no valid actual exists, values stay null
 * so the UI renders no-data placeholders instead of mock values.
 */
export const mapProgressTermApiToProgressStatusRateColumn = (
  apiData: ProgressTermApiData | null | undefined,
  activeMetric: ProgressMetricSelection,
  fallback: ProgressStatus,
): Pick<ProgressStatus, 'gauge' | 'recent'> => {
  const currentActuals = apiData && Array.isArray(apiData.currentActuals) ? apiData.currentActuals : [];
  const validActuals = currentActuals.filter(isValidActual);

  const latest = validActuals.reduce<ProgressTermApiActual | null>((best, current) => {
    if (!best) return current;
    return normalizeDateKey(current.announcementDate) >= normalizeDateKey(best.announcementDate)
      ? current
      : best;
  }, null);

  if (!latest) {
    return {
      gauge: { quarterLabel: null, percent: null },
      recent: {
        metricLabel: activeMetric.displayLabel,
        value: null,
        progressPercent: null,
      },
    };
  }

  return {
    gauge: {
      quarterLabel: toDisplayPeriodLabel(latest.period, fallback.chart.points),
      percent: toNullableNumber(latest.progressRate),
    },
    recent: {
      metricLabel: activeMetric.displayLabel,
      value: toProgressTermValue(latest),
      progressPercent: toNullableNumber(latest.progressRate),
    },
  };
};

// --- Progress term (matrix tables) -----------------------------------------

/**
 * Column order for both Progress Status matrix tables. The API returns rows
 * keyed by `period`, so cells are looked up by period instead of array index.
 */
const PROGRESS_TABLE_PERIOD_ORDER = ['q1', 'q2', 'q3', 'fullYear'] as const;

/** Company forecast rows only carry a full-year value in the design. */
const PROGRESS_FORECAST_PERIOD = 'fullYear';

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Parses `YYYY/MM/DD[ HH:mm:ss]` (or `-` separated) into calendar parts. */
const parseApiDateParts = (
  value: string | null | undefined,
): { year: string; month: number; day: number } | null => {
  if (!value) return null;

  const match = value.trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!match) return null;

  const [, year, rawMonth, rawDay] = match;
  const month = Number(rawMonth);
  const day = Number(rawDay);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return { year, month, day };
};

/**
 * Formats an API timestamp for table display.
 * - Announcement columns/rows use unpadded `yyyy/m/d`.
 * - Company forecast dates use padded `yyyy/mm/dd` to match the Figma copy.
 */
const formatProgressApiDate = (
  value: string | null | undefined,
  options: { padded?: boolean } = {},
): string => {
  const parts = parseApiDateParts(value);
  if (!parts) return PLACEHOLDER;

  return options.padded
    ? `${parts.year}/${pad2(parts.month)}/${pad2(parts.day)}`
    : `${parts.year}/${parts.month}/${parts.day}`;
};

const toProgressValueLine = (
  item: ProgressTermApiActual | ProgressTermApiConsensus | null | undefined,
): ProgressTableCellLine => ({ text: formatNumber(toProgressTermValue(item), 0) });

/**
 * Year-over-year line. Positives get an explicit `+` and positive tone,
 * negatives keep the `-` sign and negative tone, and `0` stays untoned.
 */
const toProgressYoyLine = (value: number | null | undefined): ProgressTableCellLine => {
  const yoy = toNullableNumber(value ?? null);
  if (yoy === null) return { text: PLACEHOLDER };

  const text = `${yoy > 0 ? '+' : ''}${formatNumber(yoy, 2)}%`;
  if (yoy > 0) return { text, tone: 'positive' };
  if (yoy < 0) return { text, tone: 'negative' };
  return { text };
};

/** `progressRate` is already a percentage from the API; never multiply by 100. */
const toProgressRateLine = (value: number | null | undefined): ProgressTableCellLine => {
  const rate = toNullableNumber(value ?? null);
  return { text: rate === null ? PLACEHOLDER : formatPercent(rate, 2) };
};

const toProgressForecastDateLine = (
  value: string | null | undefined,
): ProgressTableCellLine => {
  const formatted = formatProgressApiDate(value, { padded: true });
  return { text: formatted === PLACEHOLDER ? PLACEHOLDER : `(${formatted})` };
};

const toNoDataCell = (lineCount: number): ProgressTableCellLine[] =>
  Array.from({ length: Math.max(lineCount, 1) }, () => ({ text: PLACEHOLDER }));

const buildEarningsDateCells = (
  currentByPeriod: Map<string, ProgressTermApiActual>,
): ProgressTableCellLine[][] =>
  PROGRESS_TABLE_PERIOD_ORDER.map((period) => [
    { text: formatProgressApiDate(currentByPeriod.get(period)?.announcementDate) },
  ]);

const buildCurrentActualCells = (
  currentByPeriod: Map<string, ProgressTermApiActual>,
): ProgressTableCellLine[][] =>
  PROGRESS_TABLE_PERIOD_ORDER.map((period) => {
    const actual = currentByPeriod.get(period);

    return [
      toProgressValueLine(actual),
      toProgressYoyLine(actual?.yoyChange),
      toProgressRateLine(actual?.progressRate),
    ];
  });

const buildCompanyForecastCells = (
  forecast: ProgressTermApiCompanyForecast | null | undefined,
): ProgressTableCellLine[][] =>
  PROGRESS_TABLE_PERIOD_ORDER.map((period) => {
    // Quarterly columns have no forecast value in this design.
    if (period !== PROGRESS_FORECAST_PERIOD) return toNoDataCell(2);

    return [
      { text: formatNumber(toNullableNumber(forecast?.value ?? null), 0) },
      toProgressForecastDateLine(forecast?.announcementDate),
      toProgressYoyLine(forecast?.yoyChange),
    ];
  });

const buildConsensusCells = (
  consensusByPeriod: Map<string, ProgressTermApiConsensus>,
): ProgressTableCellLine[][] =>
  PROGRESS_TABLE_PERIOD_ORDER.map((period) => {
    const consensus = consensusByPeriod.get(period);
    return [toProgressValueLine(consensus), toProgressYoyLine(consensus?.yoyChange)];
  });

const buildPreviousActualCells = (
  previousByPeriod: Map<string, ProgressTermApiActual>,
): ProgressTableCellLine[][] =>
  PROGRESS_TABLE_PERIOD_ORDER.map((period) => {
    const actual = previousByPeriod.get(period);
    return [toProgressValueLine(actual), toProgressRateLine(actual?.progressRate)];
  });

const toProgressFiscalPeriodLabel = (
  apiData: ProgressTermApiData | null | undefined,
): string => {
  const fiscalYearMonth = apiData?.fiscalYearMonth?.trim() ?? '';
  const consolidatedFlag = apiData?.consolidatedFlag?.trim() ?? '';
  const label = `${fiscalYearMonth}${consolidatedFlag}`;

  return label || PLACEHOLDER;
};

/** Clones a fallback row, keeping labels/nodeId/height but replacing cells. */
const withMappedCells = (
  row: ProgressTableRow,
  cells: ProgressTableCellLine[][],
): ProgressTableRow => ({
  ...row,
  label: row.label.map((line) => ({ ...line })),
  cells,
});

const withActualFiscalPeriodLabel = (
  row: ProgressTableRow,
  apiData: ProgressTermApiData | null | undefined,
): ProgressTableRow => ({
  ...row,
  label: row.label.map((line, index) => (
    index === 0
      ? { ...line, text: toProgressFiscalPeriodLabel(apiData) }
      : { ...line }
  )),
});

/**
 * Maps `/progress/term` into the two Progress Status matrix tables.
 *
 * Static structure (node ids, row keys, row labels, row heights) is preserved
 * from `fallback`; every dynamic cell comes from the API. Missing/null values
 * render `--` so a no-data instrument never shows mock numbers. `0` is a valid
 * value and renders as `0` / `0.00%`.
 */
export const mapProgressTermApiToProgressStatusTables = (
  apiData: ProgressTermApiData | null | undefined,
  fallback: ProgressStatus,
): Pick<ProgressStatus, 'operatingProfit' | 'announcementDate'> => {
  const currentActuals = apiData && Array.isArray(apiData.currentActuals)
    ? apiData.currentActuals
    : [];
  const previousActuals = apiData && Array.isArray(apiData.previousActuals)
    ? apiData.previousActuals
    : [];
  const consensus = apiData && Array.isArray(apiData.consensus) ? apiData.consensus : [];

  const currentByPeriod = toProgressApiMap(currentActuals);
  const previousByPeriod = toProgressApiMap(previousActuals);
  const consensusByPeriod = toProgressApiMap(consensus);

  const operatingProfitRows = fallback.operatingProfit.rows.map((row) => {
    switch (row.key) {
      case 'earningsDate':
        return withMappedCells(row, buildEarningsDateCells(currentByPeriod));
      case 'actual':
        return withMappedCells(
          withActualFiscalPeriodLabel(row, apiData),
          buildCurrentActualCells(currentByPeriod),
        );
      case 'latestCompany':
        return withMappedCells(
          row,
          buildCompanyForecastCells(apiData?.latestCompanyForecast),
        );
      case 'initialCompany':
        return withMappedCells(
          row,
          buildCompanyForecastCells(apiData?.initialCompanyForecast),
        );
      case 'consensus':
        return withMappedCells(row, buildConsensusCells(consensusByPeriod));
      default:
        // Unknown fallback row: keep its shape but never keep mock numbers.
        return withMappedCells(
          row,
          row.cells.map((cell) => toNoDataCell(cell.length)),
        );
    }
  });

  const announcementDateRows = fallback.announcementDate.rows.map((row) =>
    withMappedCells(row, buildPreviousActualCells(previousByPeriod)),
  );

  const operatingProfit: ProgressTableData = {
    ...fallback.operatingProfit,
    columns: [...fallback.operatingProfit.columns],
    rows: operatingProfitRows,
  };

  const announcementDate: ProgressTableData = {
    ...fallback.announcementDate,
    // Bottom-table headers are previous-period announcement dates from the API.
    columns: PROGRESS_TABLE_PERIOD_ORDER.map((period) =>
      formatProgressApiDate(previousByPeriod.get(period)?.announcementDate),
    ),
    rows: announcementDateRows,
  };

  return { operatingProfit, announcementDate };
};
